import { call, put, select, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';
import { ApiError, ApiResponse } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { quickApplyActions } from '../slices/quickApplySlice';
import { QuickApplyStep } from '../../enums';
import {
    prepareQuickApplyDataForStepValidation,
    prepareQuickApplyDataForSubmit,
    prepareQuickApplyFieldsData,
} from '../../utils';
import { quickApplyApi } from '../../api';
import { isEmptyObject } from '../../../../../base/utils';
import { QuickApplyInfoResponse } from '../../api/interfaces';
import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { vacanciesApi } from '../../../vacancies/api';
import { SimilarVacancy, ToggleFavoriteVacancyParams } from '../../../vacancies/api/interfaces';
import { __t } from '../../../../../base/utils/t';

function* getQuickApplyInfo() {
    yield put(appActions.setLoading(Loading.App));

    try {
        const response: ApiResponse<QuickApplyInfoResponse> = yield call(quickApplyApi.getQuickApplyInfo);
        if (response.data && !isEmptyObject(response.data)) {
            const data = prepareQuickApplyFieldsData(response.data);
            yield put(quickApplyActions.setQuickApplyInfo(data));
        }
        yield put(quickApplyActions.setStep(QuickApplyStep.PersonalInfo));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submit() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step, vacancyId } = yield select((state: RootState) => state.workerAppQuickApply);

    try {
        const dataForBackendValidation = prepareQuickApplyDataForStepValidation(fields, step);
        yield call(quickApplyApi.validateQuickApplyInfo, dataForBackendValidation);

        if (step === QuickApplyStep.TextDescription) {
            const quickApplyInfo = prepareQuickApplyDataForSubmit(fields);
            const response: ApiResponse<QuickApplyInfoResponse> = yield call(
                quickApplyApi.submitQuickApplyInfo,
                quickApplyInfo,
            );
            yield call(quickApplyApi.submitQuickApply, vacancyId, { quickApplyInfoId: response.data.id });

            if (window.dataLayer) {
                window.dataLayer.push({ event: 'quick_applied' });
            }
        }

        yield put(quickApplyActions.setStep(step + 1));
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(quickApplyActions.setErrors(e.errors));
                yield put(quickApplyActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* getSimilarVacancies({ payload: vacancyId }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));

    try {
        const response: ApiResponse<SimilarVacancy[]> = yield call(vacanciesApi.getSimilarVacancies, vacancyId);
        yield put(quickApplyActions.setSimilarVacancies(response.data));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* similarVacanciesFavoritesToggle({ payload }: PayloadAction<ToggleFavoriteVacancyParams>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(vacanciesApi.favoritesToggle, payload.id, payload.value);
        yield put(
            quickApplyActions.similarVacanciesFavoritesToggleSuccess({
                id: payload.id,
                value: payload.value,
            }),
        );
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: payload.value ? __t('Вакансия добавлена в избранное') : __t('Вакансия удалена из избранного'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* quickApplySaga() {
    yield takeLatest(quickApplyActions.getQuickApplyInfo, getQuickApplyInfo);
    yield takeLatest(quickApplyActions.submit, submit);
    yield takeLatest(quickApplyActions.getSimilarVacancies, getSimilarVacancies);
    yield takeLatest(quickApplyActions.similarVacanciesFavoritesToggleRequest, similarVacanciesFavoritesToggle);
}
